import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

interface MultiLang {
  en: string;
  th: string;
  zh?: string | null;
}

interface MenuCategory {
  _id: string;
  name: MultiLang;
  description?: MultiLang;
  displayOrder: number;
  imageUrl?: string;
  isActive: boolean;
}

interface ModifierOption {
  _id: string;
  name: MultiLang;
  priceAdjustment: number;
  isDefault?: boolean;
}

interface ModifierGroup {
  _id: string;
  name: MultiLang;
  type: 'size' | 'sweetness' | 'topping' | 'addon' | 'coffee_strength';
  options: ModifierOption[];
  minSelection: number;
  maxSelection: number;
  isRequired: boolean;
}

interface MenuItem {
  _id: string;
  name: MultiLang;
  description?: MultiLang;
  categoryId: string;
  basePrice: number;
  availableSizes: ('small' | 'medium' | 'large')[];
  sizePricing?: Record<string, number>;
  modifierGroups?: string[];
  imageUrl?: string;
  isTrending: boolean;
  isAvailable: boolean;
  isRecommended?: boolean;
  tags?: string[];
  nutritionInfo?: {
    calories?: number;
    sugar?: number;
    caffeine?: number;
  };
}

// Fetch all categories
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ categories: MenuCategory[] }>>(
        '/menu/categories'
      );
      return response.data.data.categories;
    },
  });
}

// Fetch menu items (optionally filtered by category or branch)
export function useMenuItems(categoryId?: string, branchId?: string) {
  return useQuery({
    queryKey: ['menu-items', categoryId, branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (categoryId) params.append('categoryId', categoryId);
      if (branchId) params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<{ items: MenuItem[] }>>(
        `/menu/items?${params.toString()}`
      );
      return response.data.data.items;
    },
  });
}

// Fetch single menu item
export function useMenuItem(id: string) {
  return useQuery({
    queryKey: ['menu-item', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ item: MenuItem }>>(
        `/menu/items/${id}`
      );
      return response.data.data.item;
    },
    enabled: !!id,
  });
}

// Fetch modifier groups
export function useModifiers(type?: string) {
  return useQuery({
    queryKey: ['modifiers', type],
    queryFn: async () => {
      const params = type ? `?type=${type}` : '';
      const response = await apiClient.get<APIResponse<{ modifiers: ModifierGroup[] }>>(
        `/menu/modifiers${params}`
      );
      return response.data.data.modifiers;
    },
  });
}

export type { MenuCategory, MenuItem, ModifierGroup, ModifierOption, MultiLang };
